import React from 'react';
import {
  merchantConnectionExample,
  merchantPartnershipShowcaseDescription,
  merchantPartnershipShowcaseHeading,
} from '../../content/siteMessaging';

const proofCards = [
  {
    label: 'Merchant control',
    title: 'Choose the partner',
    description:
      'Accept value only from businesses you trust and want to bring into your local circle.',
    tone: 'border-[#e7c8ab] bg-[#fff6ec]',
  },
  {
    label: 'Acceptance cap',
    title: `Start with a ${merchantConnectionExample.cap} limit`,
    description:
      'Set a dollar amount that fits your comfort level before any partner value is accepted.',
    tone: 'border-[#ecd7c5] bg-white',
  },
  {
    label: 'Local growth',
    title: 'Keep value moving nearby',
    description:
      'Give customers more places to spend while keeping the relationship rooted in WNC merchants.',
    tone: 'border-[#d5e5de] bg-[#f4fbf7]',
  },
];

const flowSteps = [
  {
    step: '1',
    title: 'Start with your own offer',
    description:
      'Lead with checkout and rewards, vouchers, or gift cards customers already understand.',
  },
  {
    step: '2',
    title: 'Add one partner',
    description:
      'Choose a nearby merchant with overlapping customers or a natural business relationship.',
  },
  {
    step: '3',
    title: 'Grow carefully',
    description:
      'Raise the cap only after the partnership proves useful for both sides.',
  },
];

interface PartnershipGrowthShowcaseProps {
  id?: string;
  badge?: string;
  headline?: string;
  description?: string;
  titleLevel?: 'h2' | 'h3' | 'h4';
  primaryCta?: string;
  onPrimaryCta?: () => void;
}

export const PartnershipGrowthShowcase: React.FC<
  PartnershipGrowthShowcaseProps
> = ({
  id,
  badge = 'Merchant partnership growth',
  headline = merchantPartnershipShowcaseHeading,
  description = merchantPartnershipShowcaseDescription,
  titleLevel = 'h2',
  primaryCta,
  onPrimaryCta,
}) => {
  const HeadingTag = titleLevel;

  return (
    <section
      id={id}
      className="rounded-[2.25rem] border border-[#ecd7c5] bg-[linear-gradient(180deg,#fffaf3_0%,#fff1e2_100%)] p-6 shadow-[0_28px_70px_rgba(130,64,29,0.12)] sm:p-8"
    >
      <div className="max-w-3xl">
        <span className="inline-flex rounded-full bg-white px-4 py-1 text-sm font-semibold text-brand-primary shadow-sm">
          {badge}
        </span>
        <HeadingTag className="mt-5 text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          {headline}
        </HeadingTag>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-600 sm:text-lg">
          {description}
        </p>
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-[1.05fr_0.95fr] xl:items-start">
        <div className="rounded-[2rem] border border-[#ead3bc] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.92),rgba(255,244,232,0.78))] p-3 shadow-[0_24px_56px_rgba(90,44,20,0.12)]">
          <div className="relative overflow-hidden rounded-[1.7rem] border border-white/70 bg-[linear-gradient(180deg,#fcf3e7_0%,#f6dfc7_100%)] px-3 pb-3 pt-4 sm:px-4 sm:pb-4">
            <div
              className="absolute inset-x-10 bottom-2 top-12 rounded-[2rem] bg-[rgba(201,98,52,0.16)] blur-3xl"
              aria-hidden="true"
            />
            <img
              src="/product/partnership-growth-ipad-tilted.png"
              alt="Tilted iPad product scene showing a merchant partnership profile and supporting partnership network tiles"
              width={1200}
              height={896}
              className="relative w-full rounded-[1.45rem] object-cover shadow-[0_22px_48px_rgba(62,30,14,0.18)]"
              loading="lazy"
            />

            <aside
              aria-label="Merchant network proof highlight"
              className="mt-5 ml-auto max-w-[20rem] rounded-[1.4rem] border border-[#e8c9ae] bg-[#fff3e4] px-5 py-5 text-left shadow-[0_22px_50px_rgba(59,27,13,0.16)]"
            >
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-brand-primary">
                Controlled growth
              </p>
              <p className="mt-2 text-lg font-semibold leading-snug text-[#1f1713]">
                Expand with one partner at a time, not a blank check.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[#5c4538]">
                The point is not to open the network blindly. It is to grow local
                acceptance with terms you can explain at the counter.
              </p>
            </aside>
          </div>
        </div>

        <div className="grid gap-4">
          {proofCards.map((card) => (
            <article
              key={card.title}
              className={`rounded-[1.5rem] border p-5 shadow-sm ${card.tone}`}
            >
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-brand-primary">
                {card.label}
              </p>
              <h4 className="mt-2 text-xl font-semibold text-neutral-900">
                {card.title}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                {card.description}
              </p>
            </article>
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {flowSteps.map((step) => (
          <article
            key={step.title}
            className="rounded-[1.5rem] border border-[#ecd7c5] bg-white/90 p-5 shadow-sm"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-sm font-semibold text-white">
              {step.step}
            </span>
            <h4 className="mt-4 text-lg font-semibold text-neutral-900">
              {step.title}
            </h4>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600">
              {step.description}
            </p>
          </article>
        ))}
      </div>

      <article className="mt-6 rounded-[1.9rem] border border-brand-primary/10 bg-white p-6 shadow-[0_20px_50px_rgba(130,64,29,0.10)] sm:p-7">
        <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-brand-primary">
          WNC example
        </p>
        <h4 className="mt-3 text-2xl font-semibold tracking-tight text-neutral-900">
          A WNC partnership can start with a cap, not a blank check.
        </h4>
        <p className="mt-3 text-base leading-relaxed text-neutral-600">
          Mountain Roast can choose to accept up to{' '}
          <strong>{merchantConnectionExample.cap}</strong> of Slope Side value,
          then bring that value back to Slope Side for wholesale orders, event
          tabs, or future redemptions.
        </p>
        <p className="mt-3 text-base leading-relaxed text-neutral-600">
          That keeps the partnership useful without making the network feel
          abstract, risky, or hard to explain to staff.
        </p>

        {primaryCta && onPrimaryCta ? (
          <button
            type="button"
            onClick={onPrimaryCta}
            className="mt-6 inline-flex items-center justify-center rounded-full bg-[#f0b748] px-6 py-3 text-sm font-semibold text-[#47260f] shadow-[0_14px_28px_rgba(240,183,72,0.24)] transition-transform hover:-translate-y-0.5"
          >
            {primaryCta}
          </button>
        ) : null}
      </article>
    </section>
  );
};

export default PartnershipGrowthShowcase;
